/**
 * Rate limit tracking from API response headers.
 *
 * Tracks X-RateLimit-* headers to enable pre-flight checks
 * before making requests.
 */

export interface RateLimitStatus {
  /** Maximum requests allowed in the window */
  limit: number | null;
  /** Remaining requests in the current window */
  remaining: number | null;
  /** Unix timestamp (seconds) when the rate limit resets */
  reset: number | null;
  /** Seconds until the rate limit resets */
  retryAfter: number | null;
}

export class RateLimitTracker {
  private limit: number | null = null;
  private remaining: number | null = null;
  private reset: number | null = null;
  private retryAfter: number | null = null;

  /**
   * Extract rate limit information from response headers.
   */
  updateFromHeaders(headers: Headers): void {
    const limit = headers.get('x-ratelimit-limit');
    const remaining = headers.get('x-ratelimit-remaining');
    const reset = headers.get('x-ratelimit-reset');
    const retryAfter = headers.get('retry-after');

    if (limit !== null) this.limit = parseInt(limit, 10);
    if (remaining !== null) this.remaining = parseInt(remaining, 10);
    if (reset !== null) this.reset = parseInt(reset, 10);
    if (retryAfter !== null) this.retryAfter = parseInt(retryAfter, 10);
  }

  /**
   * Check if the rate limit has been exceeded based on tracked headers.
   */
  isLimitExceeded(): boolean {
    if (this.remaining !== null && this.remaining <= 0) {
      // Check if the reset time has passed
      if (this.reset !== null) {
        const now = Math.floor(Date.now() / 1000);
        if (now >= this.reset) {
          // Reset window has passed, allow requests
          this.remaining = null;
          this.reset = null;
          this.retryAfter = null;
          return false;
        }
      }
      return true;
    }
    return false;
  }

  /**
   * Get current rate limit status.
   */
  getStatus(): RateLimitStatus {
    return {
      limit: this.limit,
      remaining: this.remaining,
      reset: this.reset,
      retryAfter: this.retryAfter,
    };
  }
}
